import * as React from 'react';

import Icon from '~/components/icon';
import Title from '~/components/title';
import { ServicesContext } from '~/providers/services-context';
import BettingActions from '~/services/actions/betting-actions';
import {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS } from
'~/services/actions/tracking-actions';
import {
  BET_ADDED,
  BETSLIP_OPEN,
  BETSLIPDATA,
  CLIENT } from
'~/stores/constants';

import ButtonSection from './components/button-section';
import MultiBetslip from './components/multi-betslip';
import EmptyBetslip from './icons/empty-betslip';
import LoadingBetslip from './icons/loading-betslip';
import type IBet from './interfaces/IBet';
import type IBookieOdds from './interfaces/IOdds';
import styles from './style/betslip.module.css';
import { comp } from './util/utils';
import store, { withStoreSlices } from './betslip.store';
import { ErrorBoundary } from 'react-error-boundary';
import { captureRemixErrorBoundaryError } from '@sentry/remix';
import { Country } from '@cx-modules/constants';

export enum BetslipType {
  single,
  multiple,
}

type BetslipContext = {
  handleClickOut: (bookie: string, betIds: number[]) => void;
  selectBookie: (idx: number) => void;
  changeStake?: (stake: number) => void;
};

interface IBetslipProps {
  betAdded?: boolean;
  betslipOpen: boolean;
  className?: string;
  country?: Country;
}

interface IBetslipState {
  betslip: IBet[];
  loading: boolean;
  selectedBookie: IBookieOdds | null;
  stake: number;
}

// @ts-ignore
export const BetslipContext = React.createContext<BetslipContext>();
BetslipContext.displayName = 'BetslipContext';

class Betslip extends React.Component<IBetslipProps, IBetslipState> {
  static contextType = ServicesContext;
  context: React.ContextType<typeof ServicesContext> = {} as React.ContextType<
    typeof ServicesContext>;


  private odds: Array<any>;
  // private preferredBookie: string | null;

  private bettingActions: BettingActions;
  private callbacks: Partial<
    Record<keyof typeof this, ((...args: unknown[]) => void)[]>> =
  {};

  private updateClient: (key: string, val: any) => void;

  constructor(
  public props: IBetslipProps,
  public state: IBetslipState)
  {
    super(props, state);

    this.state = {
      betslip: [],
      loading: true,
      selectedBookie: null,
      stake: 10
    };

    this.updateClient = store.getState().updateClient;

    this.bettingActions = new BettingActions();

    this.odds = [];
    // this.preferredBookie = null;

    if (typeof window !== 'undefined') {
      window.ocBetslip = {
        setCallbacks: (
        callbacks: Record<keyof typeof this, (...args: unknown[]) => void>) =>
        {
          Object.entries(callbacks).forEach(([key, callback]) => {
            const thisKey = key as keyof typeof this;
            this.callbacks[thisKey] = [
            ...(this.callbacks[thisKey] ?? []),
            callback];

          });
        }
      };
    }
  }

  componentDidMount() {
    try {
      window.bettingActions = {
        ...(window.bettingActions || {}),
        addOrRemoveFromBetslip: this.bettingActions.addOrRemoveFromBetslip.bind(
          this.bettingActions
        )
      };
    } catch (e) {
      console.log('Betslip.componentDidMount', e);
    }
  }

  componentDidUpdate(prevProps: IBetslipProps) {
    const { betAdded, betslipOpen } = this.props;
    const { betAdded: wasBetAdded, betslipOpen: wasBetslipOpen } = prevProps;

    const shouldUpdateData =
    betslipOpen && !wasBetslipOpen ||
    betslipOpen && betAdded && !wasBetAdded;
    const betslipClosed = !betslipOpen && wasBetslipOpen;

    if (betslipOpen) {
      this.updateClient('showAllSportsBottom', false);
    }

    if (shouldUpdateData) {
      this.setState({ loading: true }, () => this.fetchBetslipData());
    } else if (betslipClosed) {
      this.odds = [];
      this.setState({
        betslip: [],
        loading: true,
        selectedBookie: null
      });
    }
  }

  removeBet = (betId: number, disabled?: boolean): void => {
    const { selectedBookie } = this.state;
    const tempBetslip: Array<any> = [];
    for (let i = 0; i < this.state.betslip.length; i++) {
      let betWasRemoved = true;
      if (this.state.betslip[i].betId !== betId) {
        betWasRemoved = false;
        tempBetslip.push(this.state.betslip[i]);
      }

      if (betWasRemoved) {
        this.bettingActions.addOrRemoveFromBetslip([{ betId }], [betId]);
      }
    }
    if (!disabled) {
      let bookieExists = false;
      tempBetslip.forEach((bet) => {
        bookieExists = bet.odds.some(
          (odds: {bookmakerCode: string;}) =>
          odds.bookmakerCode === (selectedBookie?.bookmakerCode ?? '')
        );
      });

      if (!bookieExists) {
        this.setState({ selectedBookie: null });
        this.fetchBetslipData();
      }
    }

    this.callbacks.removeBet?.forEach((callback) => callback(betId));
    this.updateClient('visibleHeader', true);
  };

  clearBetslip = (): void => {
    const props = {
      event: TRACKING_EVENTS.BET_SLIP_CLEAR,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_CLEAR],
      eventLabel: ''
    };
    this.context.trackingActions.eventTracking(props);
    props.event = TRACKING_EVENTS.BET_SLIP_CLOSE;
    props.eventAction = TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_CLOSE];
    this.context.trackingActions.eventTracking(props);
    this.updateClient(BETSLIP_OPEN, false);

    // Betslip needs to close before empty state is displayed
    setTimeout(() => {
      this.callbacks.clearBetslip?.forEach((callback) => callback());
      this.bettingActions.clearBetslip(() => {
        this.odds = [];
        this.setState({
          betslip: []
        });
      });
    }, 300);
  };

  private handleBetslipToggle = (): void => {
    const { betslipOpen } = this.props;
    const event = !betslipOpen ?
    TRACKING_EVENTS.BET_SLIP_OPEN :
    TRACKING_EVENTS.BET_SLIP_CLOSE;

    this.context.trackingActions.eventTracking({
      event,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[event],
      eventLabel: ''
    });
    this.updateClient(BETSLIP_OPEN, !betslipOpen);
  };

  private fetchBetslipData(): void {
    const betslipCookieArray = this.bettingActions.getBetslipCookieArray();
    const numberOfBets = betslipCookieArray.length;
    const betslipHasMultipleBets = numberOfBets > 1;
    const betslipHasSingleBet = numberOfBets === 1;

    if (betslipHasMultipleBets) {
      let betIds = betslipCookieArray.map((bet) => parseInt(bet[0], 10));
      this.bettingActions.getMultiBetslipAPI(
        betIds,
        this.context.countryCode,
        this.context.state,
        (status, res) => {
          if (status.success && res.data[BETSLIPDATA]) {
            this.storeBetslipData(res.data[BETSLIPDATA].bets);
          }
          this.setState({
            loading: false
          });
        }
      );
    } else if (betslipHasSingleBet) {
      const betIds = betslipCookieArray.map((bet) => parseInt(bet[0], 10));
      this.bettingActions.getSingleBetslipAPI(
        betIds,
        this.context.countryCode,
        this.context.state,
        (status, res) => {
          if (status.success && res.data[BETSLIPDATA]) {
            this.storeBetslipData(res.data[BETSLIPDATA].bets);
          }
          this.setState({
            loading: false
          });
        }
      );
    } else {
      this.setState({ betslip: [], loading: false });
    }
    this.updateClient(BET_ADDED, false);
  }

  private storeBetslipData(betslip: IBet[]): void {
    this.odds = this.bettingActions.calculateOddsAndBestBookie(
      betslip,
      betslip.length === 1 ? 0 : 1
    );

    if (!this.odds || this.odds.length < 1) {
      this.setState({ betslip: [] });
      return;
    }

    let selectedBookie = null;

    if (betslip.length === 1) {
      const bestOddsBookmakers: string[] = this.odds.
      filter((odds) => odds.oddsDecimal === betslip[0].bestOddsDecimal).
      map((o) => o.bookmakerCode);

      const bestOddsBookieCode = bestOddsBookmakers?.[0];

      selectedBookie = this.odds.find(
        (odd) => odd.bookmakerCode === bestOddsBookieCode
      );
    } else if (this.odds.length > 0) {
      let bookiePosition = this.odds[0].bookmakerType === 'exchange' ? 1 : 0;

      // NOTE: this block will never run
      // if (this.preferredBookie != null) {
      //   for (let i = 0; i < this.odds.length; i++) {
      //     if (this.odds[i].bookmakerCode === this.preferredBookie) {
      //       bookiePosition = i;
      //     }
      //   }
      // }

      // if the only odds are for an exchange, pick that one
      if (this.odds.length == 1 && this.odds[0].bookmakerType === 'exchange') {
        selectedBookie = this.odds[0];
      } else {
        selectedBookie = this.state.selectedBookie ?
        this.odds.find(
          (odd) =>
          this.state.selectedBookie &&
          odd.bookmakerCode === this.state.selectedBookie.bookmakerCode
        ) :
        this.odds[bookiePosition];
      }
    } else {
      this.odds = [];
    }

    this.setState({
      betslip,
      selectedBookie
    });
  }

  private handleClickOut = (bookie: string, betIds: number[]): void => {
    this.bettingActions.placeBet(bookie, betIds);
  };

  private selectBookie = (idx?: number): void => {
    if (!idx) return;
    this.setState({
      selectedBookie: this.odds[idx]
    });
    this.context.trackingActions.eventTracking({
      event: TRACKING_EVENTS.CHANGE_BOOKIE_SELECTION,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.CHANGE_BOOKIE_SELECTION]
    });
  };

  private renderEmptyBetslip = (): JSX.Element => {
    const { betslipOpen } = this.props;

    return (
      <div
        className={comp(styles.emptyBetslip, betslipOpen ? styles.open : '')}>

				<div className={styles.iconWrapper}>
					<EmptyBetslip />
				</div>

				<Title
          className={styles.title}
          title={this.context.labelActions.get('NOTHING_TO_SEE_HERE')} />


				<p className={styles.info}>
					{this.context.labelActions.get('BETSLIP_IS_EMPTY')}
				</p>
			</div>);

  };

  private renderBetslipContent = (): JSX.Element => {
    const { betslip, loading, selectedBookie, stake } = this.state;
    const { betslipOpen } = this.props;

    if (loading) {
      return (
        <div
          className={comp(styles.emptyBetslip, betslipOpen ? styles.open : '')}>

					<div className={styles.loadingIconWrapper}>
						<LoadingBetslip />
					</div>

					<Title
            className={styles.title}
            title={this.context.labelActions.get('ALMOST_THERE')} />


					<p className={styles.info}>
						{this.context.labelActions.get('LOADING_BETS')}
					</p>
				</div>);

    }

    if (betslip.length >= 1 && selectedBookie) {
      return (
        <MultiBetslip
          bets={betslip}
          bookieOdds={this.odds}
          removeBet={this.removeBet}
          selectedBookie={selectedBookie}
          setStake={(stake) => this.setState({ stake })}
          stake={stake} />);


    }

    return this.renderEmptyBetslip();
  };

  private renderTitle = () => {
    const { betslip } = this.state;

    switch (true) {
      case betslip.length === 1:
        return this.context.labelActions.get('BETSLIP_SINGLE');
      case betslip.length > 1:
        return this.context.labelActions.get('BETSLIP_MULTI');
      default:
        return this.context.labelActions.get('BETSLIP');
    }
  };

  render(): JSX.Element {
    const { betslip } = this.state;
    const { betslipOpen } = this.props;

    return (
      <ErrorBoundary
        fallbackRender={({ error }) => {
          captureRemixErrorBoundaryError(error);
          return null;
        }}>

				<div
          className={comp(
            styles.betslipContainer,
            betslipOpen ? styles.open : '',
            this.props.className ? this.props.className : ''
          )}>

					<div className={styles.header}>
						<div className={styles.content}>
							<span className={styles.counter}>{betslip?.length}</span>
							<span className={styles.title}>{this.renderTitle()}</span>
						</div>
						<span
              className={styles.closeIcon}
              data-testid="close-betslip"
              onClick={this.handleBetslipToggle}>

							<Icon icon="Close" height={32} width={32} />
						</span>
					</div>
					<BetslipContext.Provider
            value={{
              changeStake: (stake) => this.setState({ stake }),
              handleClickOut: this.handleClickOut,
              selectBookie: this.selectBookie
            }}>

						{this.renderBetslipContent()}
						<ButtonSection
              clearBetslip={this.clearBetslip}
              numberOfBets={betslip.length}
              bets={betslip}
              bookie={this.state.selectedBookie} />

					</BetslipContext.Provider>
				</div>
			</ErrorBoundary>);

  }
}

const BetslipWithStoreSlices = withStoreSlices(Betslip, {
  betslipOpen: [CLIENT, BETSLIP_OPEN],
  betAdded: [CLIENT, BET_ADDED]
});
export default BetslipWithStoreSlices;